// Staff upload for the rebounding board: drop a practice sheet's CSV, check each name's player, confirm.
// Built from the board's design system (ui.tsx, reboundingModel PAL): same header, ground, type and row grammar,
// with the review table as the page. Two tabs: UPLOAD (drop a sheet, review, save; the upload history with Edit and
// Delete) and PLAYERS (hide / show players on the board).
//
// Three ways to fill the review table:
// - a file: the backend detects the sheet (crashing or box-outs), parses raw counts and suggests a player per name
//   (saved alias, then roster first name, last name, unique prefix). The counts written are re-parsed from the file
//   server-side, so they show read-only; this page supplies the date, the label and each name's player.
// - Edit (from the upload history): the saved upload reopened, numbers editable. A game's crashing sheet comes from
//   tracking; saving an edit locks the game to the person's numbers until someone reverts it to tracking.
// - A new sheet typed in: one blank row per roster player, numbers editable; blank rows aren't saved. "Chart it" on a
//   game's not-charted box-outs opens one for that game.
// Edits and typed sheets are sent as rows with their numbers. Nothing is written until Confirm. The header names the
// sheet, the event, its date and whose numbers it holds (SheetHeader). Its event picker sets any sheet to Practice or
// to one of POR's games (a sheet dated on a game day suggests that game); moving a box-out sheet onto a game charts
// the game's box-outs, moving it back leaves the game not charted. A game's crashing stays tracking's: a crashing sheet
// can't go onto a game, and a game's own crashing sheet stays on its game.
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { useLeagueApi } from "../../../data/LeagueEnvironmentProvider";
import { headshotUrl, PAL } from "./reboundingModel";
import PlayersTab from "./PlayersTab";
import SheetHeader from "./SheetHeader";
import {
  BoardGame,
  ReboundingPage,
  SheetSession,
  UploadConfirmResponse,
  UploadPreview,
  UploadRecord,
  UploadRow,
} from "./types";
import {
  errorText,
  GroundToggle,
  head14 as head14Of,
  label11 as label11Of,
  md,
  Notice,
  num,
  pill,
  RED,
  TitleBlock,
  Tone,
  toneFill,
  useGround,
  when,
  who,
} from "./ui";
import UploadHistory from "./UploadHistory";
import { eventPhrase, isGame, originOf, versus } from "./uploadLabels";
import "./rebounding.scss";

const capitalize = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

const MATCH: Record<UploadRow["how"], { label: string; tone: Tone }> = {
  alias: { label: "SAVED NAME", tone: "good" },
  first: { label: "FIRST NAME", tone: "good" },
  last: { label: "LAST NAME", tone: "good" },
  prefix: { label: "PARTIAL · CHECK", tone: "mid" },
  ambiguous: { label: "MORE THAN ONE · PICK", tone: "bad" },
  none: { label: "NO MATCH · PICK", tone: "bad" },
  saved: { label: "AS SAVED", tone: "good" },
  skipped: { label: "SKIPPED", tone: "none" },
  roster: { label: "", tone: "none" },
};

const COUNT_COLS: Record<
  ReboundingPage,
  { key: string; head: string; w: number }[]
> = {
  crashing: [
    { key: "crashes", head: "CRASHES", w: 84 },
    { key: "crash_opps", head: "OPPS", w: 64 },
  ],
  boxouts: [
    { key: "box_outs", head: "BOX-OUTS", w: 86 },
    { key: "box_out_attempts", head: "ATT", w: 64 },
    { key: "drb", head: "DREB", w: 64 },
    { key: "oreb_allowed", head: "OREB ALW", w: 96 },
  ],
};

const WHOLE = /^\d+$/;

const ReboundingUpload: React.FC = () => {
  const api = useLeagueApi();
  const [ground, setGround] = useGround();
  const P = PAL[ground];
  const [file, setFile] = useState<{ name: string; text: string } | null>(null);
  const [preview, setPreview] = useState<UploadPreview | null>(null);
  const [date, setDate] = useState("");
  const [label, setLabel] = useState("");
  // undefined = not chosen yet, null = skip the row
  const [assign, setAssign] = useState<
    Record<string, string | null | undefined>
  >({});
  const [remember, setRemember] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<
    (UploadConfirmResponse & { edited: boolean }) | null
  >(null);
  const [uploads, setUploads] = useState<UploadRecord[]>([]);
  const [games, setGames] = useState<BoardGame[]>([]);
  // The event the sheet is set to: a POR game's session id, or null for the practice on `date`.
  const [eventGame, setEventGame] = useState<string | null>(null);
  // "Keep as practice" on the same-day-game suggestion.
  const [suggestOff, setSuggestOff] = useState(false);
  // The upload reopened for editing; saving replaces it.
  const [editing, setEditing] = useState<UploadRecord | null>(null);
  // A new sheet being typed in (no file).
  const [typing, setTyping] = useState<ReboundingPage | null>(null);
  // Editable numbers (edit and typed modes), as typed: sheet name -> count key -> text.
  const [counts, setCounts] = useState<Record<string, Record<string, string>>>(
    {},
  );
  const [tab, setTab] = useState<"upload" | "players">("upload");
  const [dragging, setDragging] = useState(false);
  const [noPhoto, setNoPhoto] = useState<Set<string>>(() => new Set());
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [deleted, setDeleted] = useState<string | null>(null);

  const loadUploads = useCallback(() => {
    api
      .getReboundingUploads()
      .then((d) => {
        setUploads(d.uploads);
        setGames(d.games ?? []);
      })
      .catch(() => undefined);
  }, [api]);
  useEffect(loadUploads, [loadUploads]);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setAssign({});
    setRemember(new Set());
    setError(null);
    setDate("");
    setLabel("");
    setEditing(null);
    setTyping(null);
    setCounts({});
    setEventGame(null);
    setSuggestOff(false);
  };

  const loadReview = (p: UploadPreview) => {
    setPreview(p);
    setCounts(
      Object.fromEntries(
        p.rows.map((r) => [
          r.sheet_name,
          Object.fromEntries(
            COUNT_COLS[p.sheet].map((c) => {
              const v = (r as any)[c.key];
              return [c.key, v == null ? "" : String(v)];
            }),
          ),
        ]),
      ),
    );
    setDate(p.date ?? "");
    setLabel(isGame(p) ? "" : (p.label ?? ""));
    setEventGame(isGame(p) && p.sessionId ? p.sessionId : null);
    // A reopened upload brings each name as saved; a name saved as skipped stays skipped (null).
    setAssign(
      Object.fromEntries(
        p.rows.map((r) => [
          r.sheet_name,
          r.how === "skipped" ? null : (r.player_id ?? undefined),
        ]),
      ),
    );
  };

  const openEdit = async (u: UploadRecord) => {
    reset();
    setDone(null);
    setBusy(true);
    try {
      const p = await api.reopenReboundingUpload(u.uploadId);
      setFile({ name: p.filename ?? "sheet.csv", text: p.csv });
      loadReview(p);
      setEditing(u);
      rootRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const removeUpload = async (u: UploadRecord) => {
    setError(null);
    try {
      const res = await api.deleteReboundingUpload(u.uploadId);
      setDeleted(
        res.rowsRemoved +
          ` ${res.sheet === "crashing" ? "crashing" : "box-out"} rows removed from ${eventPhrase(u)}.`,
      );
      if (editing?.uploadId === u.uploadId) reset();
    } catch (e) {
      setError(errorText(e));
    }
    loadUploads();
  };

  const revertUpload = async (u: UploadRecord) => {
    setError(null);
    try {
      const res = await api.revertReboundingUpload(u.uploadId);
      setDeleted(
        `Back to tracking's numbers for ${eventPhrase(u)} (${res.rows} players). Later imports of the game refresh it again.`,
      );
      if (editing?.uploadId === u.uploadId) reset();
    } catch (e) {
      setError(errorText(e));
    }
    loadUploads();
  };

  // A blank sheet; for a game (Chart it), that game's, saved to the game.
  const newSheet = async (sheet: ReboundingPage, game?: BoardGame) => {
    reset();
    setDone(null);
    setDeleted(null);
    setBusy(true);
    try {
      const p = await api.getReboundingBlankSheet(sheet, game?.sessionId);
      setFile({ name: "Typed in Explorer", text: "" });
      loadReview(p);
      setTyping(sheet);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const readFile = async (f: File) => {
    reset();
    setDone(null);
    setDeleted(null);
    const text = await f.text();
    setFile({ name: f.name, text });
    setBusy(true);
    try {
      loadReview(await api.previewReboundingUpload(text, f.name));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const byId = useMemo(
    () =>
      Object.fromEntries((preview?.roster ?? []).map((p) => [p.playerId, p])),
    [preview],
  );
  const editable = !!editing || !!typing;
  const keys = preview ? COUNT_COLS[preview.sheet].map((c) => c.key) : [];
  // In editable modes a row is blank (not saved), filled, or partly filled / not a whole number (blocks saving).
  const rowState = (r: UploadRow): "blank" | "filled" | "partial" => {
    if (!editable) return "filled";
    const vals = keys.map((k) => (counts[r.sheet_name]?.[k] ?? "").trim());
    if (vals.every((v) => v === "")) return "blank";
    return vals.every((v) => WHOLE.test(v)) ? "filled" : "partial";
  };
  const partial = preview?.rows.filter((r) => rowState(r) === "partial") ?? [];
  const toSave =
    preview?.rows.filter(
      (r) => rowState(r) === "filled" && !!assign[r.sheet_name],
    ) ?? [];
  const unresolved =
    preview?.rows.filter(
      (r) => assign[r.sheet_name] === undefined && rowState(r) !== "blank",
    ) ?? [];
  const chosen =
    preview?.rows
      .map((r) => assign[r.sheet_name])
      .filter((p): p is string => !!p) ?? [];
  const dupes = new Set(chosen.filter((p, i) => chosen.indexOf(p) !== i));
  const gameById = useMemo(
    () => Object.fromEntries(games.map((g) => [g.sessionId, g])),
    [games],
  );
  // The game the sheet is set to (the reopened game itself if the list doesn't carry it), else the practice on `date`.
  const targetGame: SheetSession | undefined = eventGame
    ? (gameById[eventGame] ??
      (preview?.sessionId === eventGame ? preview : undefined))
    : undefined;
  const game = !!targetGame;
  const event: SheetSession = targetGame
    ? { ...targetGame, kind: "game" }
    : { kind: "practice", date: date || null, label: label || null };
  const crash = preview?.sheet === "crashing";
  // A game's crashing comes from tracking: only an edit of that game's own crashing sheet saves to it, and that sheet
  // can't leave its game.
  const ownGameCrash =
    !!editing && isGame(editing) && editing.sheet === "crashing";
  const crashOntoGame =
    crash && game && !(ownGameCrash && editing?.sessionId === eventGame);
  // A practice sheet dated on a game day: suggest the game, the user confirms.
  const sameDayGame =
    !game && !suggestOff && !ownGameCrash && date
      ? games.find((g) => g.date === date)
      : undefined;
  const gameBoxRows =
    game && !crash && editing?.sessionId !== eventGame
      ? (gameById[eventGame!]?.boxoutRows ?? 0)
      : 0;
  const moved =
    !!editing &&
    (isGame(editing)
      ? editing.sessionId !== eventGame
      : game || date !== editing.date);
  const canConfirm =
    !!preview &&
    (game || !!date) &&
    !crashOntoGame &&
    !unresolved.length &&
    !dupes.size &&
    !partial.length &&
    !!toSave.length &&
    !busy;

  const pickPlayer = (r: UploadRow, value: string) => {
    const id = value === "" ? undefined : value === "skip" ? null : value;
    setAssign((a) => ({ ...a, [r.sheet_name]: id }));
    // A name the staff resolved by hand is worth remembering; a confirmed suggestion already matches.
    setRemember((s) => {
      const next = new Set(s);
      if (id && id !== r.player_id) next.add(r.sheet_name);
      else next.delete(r.sheet_name);
      return next;
    });
  };

  const confirm = async () => {
    if (!preview || !file) return;
    setBusy(true);
    setError(null);
    try {
      const common = {
        filename: file.name,
        date: targetGame?.date ?? date,
        label: game ? null : label || null,
        ...(editing ? { replaces: editing.uploadId } : {}),
        ...(eventGame ? { session: eventGame } : {}),
      };
      const res = await api.confirmReboundingUpload(
        editable
          ? {
              ...common,
              sheet: preview.sheet,
              rows: preview.rows.map((r) => ({
                sheet_name: r.sheet_name,
                player_id: assign[r.sheet_name] ?? null,
                ...counts[r.sheet_name],
              })),
            }
          : {
              ...common,
              csv: file.text,
              assignments: Object.fromEntries(
                preview.rows.map((r) => [
                  r.sheet_name,
                  assign[r.sheet_name] ?? null,
                ]),
              ),
              remember: Array.from(remember),
            },
      );
      setDone({ ...res, edited: !!editing });
      reset();
      loadUploads();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const th = preview?.thresholds;
  const F = preview?.boxout_score;
  // The tile each row will get on the board, so the reviewer sees the outcome before saving.
  const value = (r: UploadRow, key: string): number =>
    editable
      ? Number(counts[r.sheet_name]?.[key] || 0)
      : ((r as any)[key] ?? 0);
  const result = (
    row: UploadRow,
  ): { val: string; count: string; tone: Tone } => {
    if (!th || !F || rowState(row) !== "filled")
      return { val: "—", count: "", tone: "none" };
    const r = Object.fromEntries(keys.map((k) => [k, value(row, k)])) as any;
    if (crash) {
      const o = r.crash_opps ?? 0;
      const v = o ? (r.crashes ?? 0) / o : null;
      const tone: Tone =
        v == null
          ? "none"
          : v >= th.crash_rate.green
            ? "good"
            : v < th.crash_rate.red
              ? "bad"
              : "mid";
      return {
        val: v == null ? "—" : Math.round(v * 100) + "%",
        count: `${r.crashes}/${o}`,
        tone,
      };
    }
    const bo = r.box_outs ?? 0;
    const v =
      F.per_box_out * bo +
      F.per_missed * ((r.box_out_attempts ?? 0) - bo) +
      F.per_dreb * (r.drb ?? 0) +
      F.per_oreb_allowed * (r.oreb_allowed ?? 0);
    const tone: Tone =
      v >= th.boxout_score.green
        ? "good"
        : v < th.boxout_score.red
          ? "bad"
          : "mid";
    const r2 = Math.round(v * 100) / 100;
    return { val: (r2 < 0 ? "−" : "") + Math.abs(r2), count: "", tone };
  };

  const countCols = preview ? COUNT_COLS[preview.sheet] : [];
  // A typed sheet has no names to match; "remember" only applies to names read from a file.
  const showMatch = !typing;
  const showRemember = !editable;
  const cols = ["54px", "minmax(110px,170px)", "minmax(220px,300px)"]
    .concat(showMatch ? ["minmax(150px,190px)"] : [])
    .concat(countCols.map((c) => `${c.w}px`))
    .concat(["150px"])
    .concat(showRemember ? ["140px"] : [])
    .join(" ");

  const label11 = label11Of(P);
  const head14 = head14Of(P);
  const field: React.CSSProperties = {
    height: 38,
    border: `1px solid ${P.pillBorder}`,
    background: "transparent",
    color: P.fg,
    fontFamily: "var(--font-display)",
    fontWeight: 800,
    fontSize: 20,
    padding: "0 10px",
    borderRadius: 0,
    boxSizing: "border-box",
    colorScheme: ground === "Black" ? "dark" : "light",
  };
  const square = (fill: string, size = 20) => (
    <div
      style={{ width: size, height: size, background: fill, flex: "none" }}
    />
  );
  const notice = (tone: Tone, children: React.ReactNode) => (
    <Notice P={P} tone={tone}>
      {children}
    </Notice>
  );
  // A save to a practice that already holds this sheet replaces it; an edit saved to its own date replaces itself.
  const replacing =
    preview && date && !game ? (preview.sheetDates[date] ?? 0) : 0;
  const replacingOther =
    replacing > 0 && !(editing && !isGame(editing) && date === editing.date);
  // To practice from a game: a game's label isn't a practice's.
  const pickEvent = (id: string | null) => {
    setEventGame(id);
    if (!id && isGame(event)) setLabel("");
  };

  return (
    <div
      ref={rootRef}
      className="rebounding"
      style={{ background: P.bg, color: P.fg }}
    >
      <div
        style={{
          minHeight: "100%",
          background: P.bg,
          color: P.fg,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 5,
            background: P.bg,
            borderBottom: `4px solid ${RED}`,
          }}
        >
          <div
            style={{
              maxWidth: 1680,
              margin: "0 auto",
              padding: "6px 24px",
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
              boxSizing: "border-box",
            }}
          >
            <img
              src={P.logo}
              alt="Trail Blazers"
              style={{ height: 30, width: "auto" }}
            />
            <Link to="/rebounding" style={pill(P, false, 14)}>
              ← BOARD
            </Link>
            <div style={{ display: "flex" }}>
              <button
                onClick={() => setTab("upload")}
                style={pill(P, tab === "upload")}
              >
                UPLOAD
              </button>
              <button
                onClick={() => setTab("players")}
                style={pill(P, tab === "players")}
              >
                PLAYERS
              </button>
            </div>
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: 14,
              }}
            >
              <GroundToggle ground={ground} setGround={setGround} />
            </div>
          </div>
        </div>

        <div
          style={{
            maxWidth: 1680,
            width: "100%",
            margin: "0 auto",
            padding: "0 24px 48px",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            flex: 1,
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-end",
              gap: "12px 56px",
              padding: "6px 0 14px",
            }}
          >
            <TitleBlock
              eyebrow={
                tab === "players"
                  ? "REBOUNDING · WHO'S ON THE BOARD"
                  : editing
                    ? "REBOUNDING · EDITING AN UPLOAD"
                    : typing
                      ? "REBOUNDING · TYPED IN"
                      : "REBOUNDING · PRACTICE SHEETS"
              }
              title={
                tab === "players"
                  ? "PLAYERS"
                  : editing
                    ? "EDIT"
                    : typing
                      ? "NEW SHEET"
                      : "UPLOAD"
              }
            />
            {tab === "upload" && preview && (
              <SheetHeader
                P={P}
                field={field}
                sheet={preview.sheet}
                session={event}
                origin={editing ? originOf(editing) : "uploaded"}
                date={date}
                setDate={setDate}
                label={label}
                setLabel={setLabel}
                games={games}
                onEvent={ownGameCrash ? undefined : pickEvent}
              />
            )}
          </div>

          {tab === "players" && <PlayersTab P={P} />}
          {tab === "upload" && (
            <>
              {deleted && notice("good", deleted)}
              {done &&
                notice(
                  "good",
                  <>
                    {done.edited ? "Saved your changes: " : "Saved "}
                    {done.rows}{" "}
                    {done.sheet === "crashing" ? "crashing" : "box-out"} rows to{" "}
                    {eventPhrase(done)}
                    {done.skipped ? ` (${done.skipped} skipped)` : ""}.{" "}
                    <Link to="/rebounding">See the board</Link>
                  </>,
                )}
              {error && notice("bad", error)}

              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  const f = e.dataTransfer.files?.[0];
                  if (f) readFile(f);
                }}
                style={{
                  border: `2px dashed ${dragging ? RED : P.pillBorder}`,
                  background: dragging ? P.zebra : "transparent",
                  padding: preview ? "10px 16px" : "36px 24px",
                  marginBottom: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 18,
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    flex: "1 1 320px",
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      fontFamily: "var(--font-display)",
                      fontWeight: 900,
                      fontSize: preview ? 22 : 36,
                      lineHeight: 1,
                    }}
                  >
                    {typing
                      ? `NEW ${typing === "crashing" ? "CRASHING" : "BOX-OUT"} SHEET${game ? ` · ${eventPhrase(event).toUpperCase()}` : ""}`
                      : editing && !editing.filename
                        ? originOf(editing) === "tracking"
                          ? "FROM TRACKING"
                          : "TYPED IN EXPLORER"
                        : file
                          ? file.name.toUpperCase()
                          : "DROP A PRACTICE SHEET"}
                  </span>
                  {typing && (
                    <span style={{ fontSize: 15, color: P.sub }}>
                      Type each player's numbers. Leave a row blank if he wasn't
                      {game ? " in the game" : " at practice"}; blank rows
                      aren't saved.
                    </span>
                  )}
                  {editing && originOf(editing) === "tracking" && (
                    <span style={{ fontSize: 15, color: P.sub }}>
                      From tracking, refreshed on every import of this game
                      until someone edits it. Saving locks the game to your
                      numbers; Revert to tracking in the history undoes that.
                    </span>
                  )}
                  {editing && originOf(editing) !== "tracking" && (
                    <span style={{ fontSize: 15, color: P.sub }}>
                      {originOf(editing) === "edited" ? "Edited" : "Uploaded"}{" "}
                      by {who(editing.uploadedBy)} · {when(editing.uploadedAt)}.
                      Saving replaces it; the original stays in the history.
                      {ownGameCrash
                        ? " Tracking no longer refreshes this game."
                        : ""}
                    </span>
                  )}
                  {!preview && (
                    <span style={{ fontSize: 17, color: P.sub }}>
                      {busy
                        ? "Reading the sheet…"
                        : "The crashing sheet or REB Scores, exported as CSV. Nothing saves until you confirm."}
                    </span>
                  )}
                  {!preview && !busy && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginTop: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      <span style={{ ...label11, fontSize: 12 }}>
                        NO FILE? TYPE ONE IN:
                      </span>
                      <button
                        onClick={() => newSheet("crashing")}
                        style={{ ...pill(P, false, 14), height: 30 }}
                      >
                        NEW CRASHING SHEET
                      </button>
                      <button
                        onClick={() => newSheet("boxouts")}
                        style={{ ...pill(P, false, 14), height: 30 }}
                      >
                        NEW BOX-OUT SHEET
                      </button>
                    </div>
                  )}
                </div>
                {editing || typing ? (
                  <button onClick={reset} style={pill(P, false, 16)}>
                    {editing ? "CANCEL EDIT" : "CANCEL"}
                  </button>
                ) : (
                  <button
                    onClick={() => inputRef.current?.click()}
                    style={pill(P, !preview, 16)}
                  >
                    {preview ? "CHOOSE ANOTHER" : "CHOOSE FILE"}
                  </button>
                )}
                <input
                  ref={inputRef}
                  hidden
                  type="file"
                  accept=".csv,text/csv"
                  data-testid="sheet-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) readFile(f);
                    e.target.value = "";
                  }}
                />
              </div>

              {preview && (
                <>
                  {replacingOther &&
                    notice(
                      "mid",
                      <>
                        Practice {md(date)} already has {replacing}{" "}
                        {crash ? "crashing" : "box-out"} rows. Saving replaces
                        them with this sheet.
                      </>,
                    )}
                  {sameDayGame &&
                    notice(
                      "mid",
                      <span data-testid="game-suggestion">
                        Portland played {versus(sameDayGame)} on{" "}
                        {md(sameDayGame.date)}.{" "}
                        {crash
                          ? "A game's crashing comes from tracking: to change it, edit the game's crashing sheet in the history below. This sheet saves as a practice."
                          : "Is this sheet the game's box-outs?"}{" "}
                        {!crash && (
                          <button
                            onClick={() => pickEvent(sameDayGame.sessionId)}
                            style={{ ...pill(P, true, 13), height: 28 }}
                          >
                            YES, THE GAME
                          </button>
                        )}{" "}
                        <button
                          onClick={() => setSuggestOff(true)}
                          style={{ ...pill(P, false, 13), height: 28 }}
                        >
                          {crash ? "OK" : "NO, A PRACTICE"}
                        </button>
                      </span>,
                    )}
                  {crashOntoGame &&
                    notice(
                      "bad",
                      <span data-testid="crash-guard">
                        A game's crashing comes from tracking, so this sheet's
                        crashing numbers won't replace it. To change{" "}
                        {eventPhrase(event)}, edit its crashing sheet in the
                        history below, or set this sheet back to Practice.
                      </span>,
                    )}
                  {gameBoxRows > 0 &&
                    notice(
                      "mid",
                      <>
                        {capitalize(eventPhrase(event))} already has{" "}
                        {gameBoxRows} box-out rows. Saving replaces them with
                        this sheet.
                      </>,
                    )}
                  {moved &&
                    !crashOntoGame &&
                    notice(
                      "mid",
                      <span data-testid="moving">
                        Moving this sheet from {eventPhrase(editing!)} to{" "}
                        {eventPhrase(event)}.
                        {isGame(editing!) && editing!.sheet === "boxouts"
                          ? " The game's box-outs go back to not charted."
                          : ""}
                      </span>,
                    )}
                  {!preview.date &&
                    !editable &&
                    !game &&
                    notice(
                      "mid",
                      "This sheet has no date on it. Set the practice date above before saving.",
                    )}
                  {preview.warnings.map((w) => (
                    <React.Fragment key={w}>{notice("mid", w)}</React.Fragment>
                  ))}

                  <div style={{ overflowX: "auto" }}>
                    <div style={{ minWidth: "max-content" }}>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: cols,
                          columnGap: 14,
                          height: 30,
                          alignItems: "end",
                          paddingBottom: 5,
                          borderBottom: `2px solid ${P.headRule}`,
                          ...head14,
                        }}
                      >
                        <span />
                        <span>{typing ? "NAME" : "SHEET NAME"}</span>
                        <span>PLAYER</span>
                        {showMatch && <span>MATCH</span>}
                        {countCols.map((c) => (
                          <span
                            key={c.key}
                            style={{ textAlign: "right", whiteSpace: "nowrap" }}
                          >
                            {c.head}
                          </span>
                        ))}
                        <span style={{ color: P.fg }}>ON THE BOARD</span>
                        {showRemember && (
                          <span title="Save this sheet name as a nickname for the player you picked, so the next sheet that uses it matches on its own">
                            REMEMBER NAME
                          </span>
                        )}
                      </div>
                      {preview.rows.map((r, i) => {
                        const pid = assign[r.sheet_name];
                        const player = pid ? byId[pid] : undefined;
                        const edited = pid !== undefined && pid !== r.player_id;
                        const match = edited
                          ? {
                              label: pid === null ? "SKIPPED" : "PICKED",
                              tone: (pid === null ? "none" : "plain") as Tone,
                            }
                          : MATCH[r.how];
                        const dup = !!pid && dupes.has(pid);
                        const res = result(r);
                        const photo =
                          player && !noPhoto.has(player.nbaId)
                            ? headshotUrl(player.nbaId)
                            : null;
                        const canRemember = !!pid && r.how !== "alias";
                        return (
                          <div
                            key={r.sheet_name}
                            style={{
                              display: "grid",
                              gridTemplateColumns: cols,
                              columnGap: 14,
                              minHeight: 56,
                              alignItems: "center",
                              borderBottom: `1px solid ${P.rule}`,
                              background: i % 2 ? P.zebra : "transparent",
                              margin: "0 -10px",
                              padding: "0 10px",
                              opacity: pid === null ? 0.4 : 1,
                            }}
                          >
                            <div
                              style={{
                                width: 54,
                                height: 40,
                                background: P.photoBg,
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {photo ? (
                                <img
                                  src={photo}
                                  alt=""
                                  onError={() =>
                                    setNoPhoto((s) =>
                                      new Set(s).add(player!.nbaId),
                                    )
                                  }
                                  style={{
                                    width: 54,
                                    height: 40,
                                    objectFit: "cover",
                                    objectPosition: "top",
                                  }}
                                />
                              ) : (
                                <span
                                  style={{
                                    fontFamily: "var(--font-display)",
                                    fontWeight: 800,
                                    fontSize: 20,
                                    color: P.photoInk,
                                  }}
                                >
                                  {(player?.first ?? r.sheet_name)
                                    .slice(0, 2)
                                    .toUpperCase()}
                                </span>
                              )}
                            </div>
                            <span
                              style={{
                                fontFamily: "var(--font-display)",
                                fontWeight: 800,
                                fontSize: 30,
                                lineHeight: 1,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {r.sheet_name.toUpperCase()}
                            </span>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                              }}
                            >
                              {typing ? (
                                <span style={{ fontSize: 17, fontWeight: 700 }}>
                                  {player?.fullName ?? ""}
                                </span>
                              ) : (
                                <select
                                  value={
                                    pid === undefined
                                      ? ""
                                      : pid === null
                                        ? "skip"
                                        : pid
                                  }
                                  onChange={(e) =>
                                    pickPlayer(r, e.target.value)
                                  }
                                  aria-label={`Player for ${r.sheet_name}`}
                                  style={{
                                    ...field,
                                    width: "100%",
                                    fontSize: 18,
                                    borderColor:
                                      pid === undefined || dup
                                        ? P.bad
                                        : P.pillBorder,
                                    cursor: "pointer",
                                  }}
                                >
                                  <option value="" style={{ background: P.bg }}>
                                    Pick a player…
                                  </option>
                                  {preview.roster.map((p) => (
                                    <option
                                      key={p.playerId}
                                      value={p.playerId}
                                      style={{ background: P.bg }}
                                    >
                                      {p.fullName}
                                    </option>
                                  ))}
                                  <option
                                    value="skip"
                                    style={{ background: P.bg }}
                                  >
                                    Skip this row
                                  </option>
                                </select>
                              )}
                              {dup && (
                                <span
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: P.bad,
                                  }}
                                >
                                  ANOTHER ROW HAS THIS PLAYER
                                </span>
                              )}
                            </div>
                            {showMatch && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                {square(toneFill(P, match.tone))}
                                <span
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    letterSpacing: ".08em",
                                    color: P.sub,
                                  }}
                                >
                                  {match.label}
                                </span>
                              </div>
                            )}
                            {countCols.map((c) =>
                              editable ? (
                                <input
                                  key={c.key}
                                  inputMode="numeric"
                                  value={counts[r.sheet_name]?.[c.key] ?? ""}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setCounts((all) => ({
                                      ...all,
                                      [r.sheet_name]: {
                                        ...all[r.sheet_name],
                                        [c.key]: v,
                                      },
                                    }));
                                  }}
                                  aria-label={`${c.head} for ${r.sheet_name}`}
                                  style={{
                                    ...field,
                                    ...num,
                                    fontSize: 22,
                                    width: "100%",
                                    textAlign: "right",
                                    padding: "0 8px",
                                    borderColor:
                                      rowState(r) === "partial" &&
                                      !WHOLE.test(
                                        (
                                          counts[r.sheet_name]?.[c.key] ?? ""
                                        ).trim(),
                                      )
                                        ? P.bad
                                        : P.pillBorder,
                                  }}
                                />
                              ) : (
                                <span
                                  key={c.key}
                                  style={{ ...num, textAlign: "right" }}
                                >
                                  {(r as any)[c.key]}
                                </span>
                              ),
                            )}
                            <div
                              style={{
                                height: 46,
                                background: toneFill(P, res.tone),
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "0 8px",
                                boxSizing: "border-box",
                              }}
                            >
                              <span
                                style={{
                                  fontFamily: "var(--font-display)",
                                  fontWeight: 900,
                                  fontSize: 32,
                                  lineHeight: 1,
                                  color: "#000",
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                {res.val}
                              </span>
                              <span
                                style={{
                                  marginLeft: "auto",
                                  fontSize: 15,
                                  fontWeight: 700,
                                  color: "#000",
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                {res.count}
                              </span>
                            </div>
                            {showRemember && (
                              <button
                                role="checkbox"
                                aria-checked={remember.has(r.sheet_name)}
                                aria-label={`Remember ${r.sheet_name}`}
                                disabled={!canRemember}
                                onClick={() =>
                                  setRemember((s) => {
                                    const next = new Set(s);
                                    if (next.has(r.sheet_name))
                                      next.delete(r.sheet_name);
                                    else next.add(r.sheet_name);
                                    return next;
                                  })
                                }
                                style={{
                                  width: 26,
                                  height: 26,
                                  border: `2px solid ${canRemember ? P.sub : P.rule}`,
                                  background: remember.has(r.sheet_name)
                                    ? P.fg
                                    : "transparent",
                                  cursor: canRemember ? "pointer" : "default",
                                  borderRadius: 0,
                                  padding: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {remember.has(r.sheet_name) && (
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke={P.bg}
                                    strokeWidth="3.4"
                                    strokeLinecap="square"
                                  >
                                    <path d="M4.5 12.5L10 18L19.5 6.5" />
                                  </svg>
                                )}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div
                    style={{
                      position: "sticky",
                      bottom: 0,
                      background: P.bg,
                      borderTop: `2px solid ${P.headRule}`,
                      marginTop: 18,
                      padding: "12px 0",
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      flexWrap: "wrap",
                      zIndex: 4,
                    }}
                  >
                    <button
                      onClick={confirm}
                      disabled={!canConfirm}
                      style={{
                        ...pill(P, true, 20),
                        height: 42,
                        opacity: canConfirm ? 1 : 0.35,
                        cursor: canConfirm ? "pointer" : "default",
                      }}
                    >
                      {busy
                        ? "SAVING…"
                        : editing
                          ? "SAVE CHANGES"
                          : typing
                            ? "SAVE SHEET"
                            : "CONFIRM AND SAVE"}
                    </button>
                    <button
                      onClick={reset}
                      style={{ ...pill(P, false, 16), height: 42 }}
                    >
                      CANCEL
                    </button>
                    <span style={{ fontSize: 17, color: P.sub }}>
                      {unresolved.length
                        ? unresolved.length > 1
                          ? `${unresolved.length} names still need a player (or Skip).`
                          : `${unresolved[0].sheet_name} still needs a player (or Skip).`
                        : dupes.size
                          ? "Two rows point at the same player."
                          : partial.length
                            ? `${partial[0].sheet_name}: fill in every number, or clear the row.`
                            : !game && !date
                              ? "Set the practice date."
                              : !toSave.length
                                ? "Type at least one player's numbers."
                                : `${toSave.length} rows will be saved to ${eventPhrase(event)}.`}
                    </span>
                  </div>
                </>
              )}

              <UploadHistory
                P={P}
                uploads={uploads}
                games={games}
                editingId={editing?.uploadId ?? null}
                onEdit={openEdit}
                onDelete={removeUpload}
                onRevert={revertUpload}
                onChart={(g) => newSheet("boxouts", g)}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReboundingUpload;
